import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { buildClassAttendanceRows } from "@/artifacts/global/utils/buildAttendanceRows";
import { HStack } from "@/components/layout/HStack";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import { Button } from "@/components/ui/Button/Button";
import {
  ArrowDown01,
  ArrowDownAZ,
  ArrowUp01,
  ArrowUpAZ,
  ArrowUpDown,
  Filter,
  Info,
} from "lucide-react";
import { Input } from "@/components/ui/Input/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import { RESULTS_PER_PAGE_OPTIONS } from "@/constants/tableOptions";
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination/Pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { Switch } from "@/components/ui/Switch/Switch";
import type { ClassAttendanceRow } from "@/types/tables";

const getColumns = (
  setEntries: Dispatch<
    SetStateAction<{ studentId: string; present: boolean }[]>
  >,
): ColumnDef<ClassAttendanceRow>[] => [
  {
    accessorKey: "studentName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Student{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
  },
  {
    accessorKey: "studentEmail",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Email{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
  },
  {
    accessorKey: "studentGradeLevel",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Grade{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown01 />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp01 />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    filterFn: (row, columnId, filterValue) => {
      const selected = (filterValue as number[]) ?? [];
      if (selected.length === 0) return true;

      const v = row.getValue(columnId);
      const n = typeof v === "number" ? v : Number(v);

      return selected.includes(n);
    },
  },
  {
    accessorKey: "present",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Present{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => {
      const studentId = row.original.studentId;
      const checked = row.original.present;

      return (
        <Switch
          checked={checked}
          onCheckedChange={(val) =>
            setEntries((prev) =>
              prev.map((e) =>
                e.studentId === studentId ? { ...e, present: val } : e,
              ),
            )
          }
        />
      );
    },
    filterFn: (row, columnId, filterValue) => {
      const selected = (filterValue as ("present" | "absent")[]) ?? [];
      if (selected.length === 0) return true;

      const v = row.getValue(columnId);
      const isPresent = typeof v === "boolean" ? v : String(v) === "true";

      if (isPresent) return selected.includes("present");
      return selected.includes("absent");
    },
  },
  {
    accessorKey: "reason",
    header: () => (
      <HStack align="center" gap="0.25rem">
        Reason
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon-small" variant="ghost">
              <Info />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Reason can be assigned by administrators for specific students
          </TooltipContent>
        </Tooltip>
      </HStack>
    ),
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: ClassAttendanceRow[];
  columns: ColumnDef<ClassAttendanceRow>[];
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      columnFilters,
      pagination,
    },
  });

  // Grade Filtering
  const gradeCol = table.getColumn("studentGradeLevel");
  const selectedGrade =
    (gradeCol?.getFilterValue() as number[] | undefined) ?? [];

  const grades = useMemo(() => {
    const set = new Set<number>();
    for (const d of data) if (d.studentGradeLevel) set.add(d.studentGradeLevel);
    return Array.from(set).sort((a, b) => a - b);
  }, [data]);

  const toggleGrade = (grade: number) => {
    const next = selectedGrade.includes(grade)
      ? selectedGrade.filter((s) => s !== grade)
      : [...selectedGrade, grade];

    gradeCol?.setFilterValue(next);
  };

  const clearGradeFilter = () => {
    gradeCol?.setFilterValue([]);
  };

  // Present Filtering
  const presentCol = table.getColumn("present");
  const selectedPresent =
    (presentCol?.getFilterValue() as ("present" | "absent")[] | undefined) ??
    [];

  const togglePresent = (v: "present" | "absent") => {
    const next = selectedPresent.includes(v)
      ? selectedPresent.filter((s) => s !== v)
      : [...selectedPresent, v];

    presentCol?.setFilterValue(next);
  };

  const clearPresentFilter = () => {
    presentCol?.setFilterValue([]);
  };

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  // Pagination
  const showPagination = table.getPageCount() > 1;

  function getPageItems(current: number, total: number) {
    const c = current + 1;
    const t = total;

    if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);

    const items: (number | "…")[] = [1];

    if (c > 3) items.push("…");

    const start = Math.max(2, c - 1);
    const end = Math.min(t - 1, c + 1);

    for (let p = start; p <= end; p++) items.push(p);

    if (c < t - 2) items.push("…");

    items.push(t);
    return items;
  }

  return (
    <>
      <HStack align="center" justify="space-between" wrap>
        <HStack align="center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                style={{
                  height: "fit-content",
                  padding: "var(--space-xxs) var(--space-xs)",
                }}
              >
                <Filter /> Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Grades</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {grades.map((g) => (
                    <DropdownMenuCheckboxItem
                      key={g}
                      checked={selectedGrade.includes(g)}
                      onCheckedChange={() => toggleGrade(g)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {g}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearGradeFilter();
                    }}
                    disabled={selectedGrade.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Present</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuCheckboxItem
                    checked={selectedPresent.includes("present")}
                    onCheckedChange={() => togglePresent("present")}
                    onSelect={(e) => e.preventDefault()}
                  >
                    Present
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={selectedPresent.includes("absent")}
                    onCheckedChange={() => togglePresent("absent")}
                    onSelect={(e) => e.preventDefault()}
                  >
                    Absent
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearPresentFilter();
                    }}
                    disabled={selectedPresent.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <p
            style={{
              color: "var(--muted-foreground)",
            }}
          >
            {filteredCount} of {totalCount} students
          </p>
        </HStack>
        <HStack align="center">
          <div style={{ width: "240px", maxWidth: "100%" }}>
            <Input
              placeholder="Search students..."
              value={
                (table.getColumn("studentName")?.getFilterValue() as string) ??
                ""
              }
              onChange={(e) =>
                table.getColumn("studentName")?.setFilterValue(e.target.value)
              }
            />
          </div>
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(v) => {
              table.setPageSize(Number(v));
              table.setPageIndex(0);
            }}
          >
            <SelectTrigger
              style={{
                width: "fit-content",
                height: "fit-content",
                padding: "var(--space-xxs) var(--space-xs)",
              }}
            >
              <SelectValue placeholder="Select results per page" />
            </SelectTrigger>
            <SelectContent>
              {RESULTS_PER_PAGE_OPTIONS.filter((n) => n !== 10).map(
                (option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option} results/page
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </HStack>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
              />
            </PaginationItem>

            {getPageItems(pagination.pageIndex, table.getPageCount()).map(
              (it, idx) =>
                it === "…" ? (
                  <PaginationItem key={`el-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={it}>
                    <PaginationButton
                      isActive={it === pagination.pageIndex + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        table.setPageIndex(it - 1);
                      }}
                    >
                      {it}
                    </PaginationButton>
                  </PaginationItem>
                ),
            )}

            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};

const AttendanceTable = ({
  data,
  entries,
  setEntries,
}: {
  data: {
    student: {
      _id: string;
      name: string;
      email: string;
      gradYear: number;
    };
    present: boolean;
    reason: string;
  }[];
  entries: { studentId: string; present: boolean }[];
  setEntries: Dispatch<
    SetStateAction<{ studentId: string; present: boolean }[]>
  >;
}) => {
  const rows = useMemo(() => {
    const base = buildClassAttendanceRows(data);
    const presentById = new Map(entries.map((e) => [e.studentId, e.present]));

    return base.map((r) => ({
      ...r,
      present: presentById.get(r.studentId) ?? r.present,
    }));
  }, [data, entries]);
  const columns = useMemo(() => getColumns(setEntries), [setEntries]);

  return (
    <DashboardCard>
      <DataTable data={rows} columns={columns} />
    </DashboardCard>
  );
};

export { AttendanceTable };
